import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useResetPassword } from '../hooks/useAuth';
import { ResetPasswordRequest } from '../models/Auth';

interface ResetPasswordFormProps {
  className?: string;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const resetPasswordMutation = useResetPassword();

  const [formData, setFormData] = useState<ResetPasswordRequest>({
    email: '',
    password: '',
    token: '',
  });

  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
    token?: string;
  }>({});

  // 🎯 Obtener token de la URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setFormData((prev) => ({ ...prev, token }));
    } else {
      setFieldErrors((prev) => ({ ...prev, token: 'Token no encontrado en la URL' }));
    }
  }, [searchParams]);

  // 🎯 Validación de campos individuales
  const validateField = (name: keyof ResetPasswordRequest, value: string): string | undefined => {
    switch (name) {
      case 'email': {
        if (!value.trim()) return 'El email es requerido';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'El formato del email no es válido';
        return undefined;
      }

      case 'password':
        if (!value.trim()) return 'La contraseña es requerida';
        return undefined;

      case 'token':
        if (!value.trim()) return 'El token es requerido';
        return undefined;

      default:
        return undefined;
    }
  };

  // 🎯 Manejo de cambios en inputs
  const handleInputChange = (name: keyof ResetPasswordRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Limpiar error del campo si existe
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // 🎯 Validación completa del formulario
  const validateForm = (): boolean => {
    const errors: typeof fieldErrors = {};

    const emailError = validateField('email', formData.email);
    if (emailError) errors.email = emailError;

    const passwordError = validateField('password', formData.password);
    if (passwordError) errors.password = passwordError;

    const tokenError = validateField('token', formData.token);
    if (tokenError) errors.token = tokenError;

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 🎯 Manejo del submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await resetPasswordMutation.mutateAsync(formData);
    } catch (error) {
      // Los errores se manejan automáticamente en el hook
      console.error('Error en reset password:', error);
    }
  };

  // 🎯 Manejo de blur para validación inmediata
  const handleBlur = (name: keyof ResetPasswordRequest) => {
    const error = validateField(name, formData[name]);
    if (error) {
      setFieldErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  return (
    <div className={`max-w-96 ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 🎯 Error general */}
        {resetPasswordMutation.error && (
          <p className="text-sm text-destructive">
            {resetPasswordMutation.error.message || 'Error al restablecer la contraseña'}
          </p>
        )}

        {/* 🎯 Error de token */}
        {fieldErrors.token && (
          <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
            <p className="text-sm text-destructive">{fieldErrors.token}</p>
          </div>
        )}

        {/* 🎯 Campo Email */}
        <div className="space-y-2">
          <label htmlFor="reset-email" className="text-sm font-medium text-foreground">
            {t('form.email.name', 'Email')}
          </label>
          <Input
            id="reset-email"
            type="email"
            placeholder={t('form.email.placeholder', 'tu@email.com')}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            className={`${fieldErrors.email ? 'border-destructive' : ''}`}
            autoFocus
            autoComplete="email"
            data-testid="reset-password-email"
          />
          {fieldErrors.email && <p className="text-sm text-destructive">{fieldErrors.email}</p>}
        </div>

        {/* 🎯 Campo Contraseña */}
        <div className="space-y-2">
          <label htmlFor="reset-password" className="text-sm font-medium text-foreground">
            {t('form.newPassword.name', 'Nueva Contraseña')}
          </label>
          <Input
            id="reset-password"
            type="password"
            placeholder={t('form.newPassword.placeholder', 'Tu nueva contraseña')}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            onBlur={() => handleBlur('password')}
            className={`${fieldErrors.password ? 'border-destructive' : ''}`}
            autoComplete="new-password"
            data-testid="reset-password-password"
          />
          {fieldErrors.password && <p className="text-sm text-destructive">{fieldErrors.password}</p>}
        </div>

        {/* 🎯 Botón de envío */}
        <div className="pt-4">
          <Button
            type="submit"
            className="w-full"
            disabled={resetPasswordMutation.isPending || !formData.token}
            data-testid="reset-password-submit"
          >
            {resetPasswordMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.resetPassword.loading', 'Restableciendo...')}
              </>
            ) : (
              t('auth.resetPassword.submitButton', 'Restablecer')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordForm;
